import type { IApiKey } from '@experiences/interfaces';
import { UiText } from '@experiences/ui-common';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import FileCopyOutlinedIcon from '@mui/icons-material/FileCopyOutlined';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import { makeStyles } from '@mui/styles';
import createStyles from '@mui/styles/createStyles';
import clsx from 'clsx';
import React, {
    useEffect,
    useState,
} from 'react';
import CopyToClipboard from 'react-copy-to-clipboard';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import {
    consumableCodeToApiKeyIdentifier,
    notificationType,
} from '../../../../common/constants/Constant';
import { useUiSnackBar } from '../../../../common/hooks/useUiSnackBar';
import { isAdminSelector } from '../../../../store/selectors';
import { useCanManageLicensingData } from '../../useCanManageLicensingData';
import { visuallyHiddenStyle } from '../visuallyHiddenStyle';
import { useRegenerateMlKey } from './useRegenerateMlKey';

const API_KEY_MASK_OVERFLOW_LENGTH = 88;
const MASKED_API_KEY = '•'.repeat(API_KEY_MASK_OVERFLOW_LENGTH);

const useStyles = makeStyles(theme =>
    createStyles({
        iconButton: { color: theme.palette.semantic.colorPrimary },
        apiKeyWithAction: {
            display: 'flex',
            marginTop: '8px',
        },
        apiKey: {
            display: 'flex',
            marginTop: '8px',
            overflow: 'hidden',
        },
        apiKeyText: { whiteSpace: 'nowrap' },
        apiKeyLine: {
            textOverflow: 'ellipsis',
            maxWidth: '480px',
        },
        apiKeyActions: {
            marginLeft: '5px',
            display: 'flex',

            alignSelf: 'flex-end',
        },
        noWrap: {
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
        },
        maskedApiKey: {
            fontSize: '18px',
            lineHeight: 'var(--font-m-line-height, 20px)',
        },
        noEllipsis: { textOverflow: 'clip' },
        visuallyHidden: visuallyHiddenStyle,
    }),
);

export const UiApiKey: React.FC<{ consumableCode: string; apiKey?: IApiKey; tenantId?: string }> = ({
    consumableCode, apiKey, tenantId,
}) => {

    const { formatMessage: translate } = useIntl();
    const classes = useStyles();
    const [ showKey, setShowKey ] = useState(false);
    const { createNotification } = useUiSnackBar();
    const {
        loading, regenerate,
    } = useRegenerateMlKey(tenantId);

    useEffect(() => {
        setShowKey(false);
    }, [ apiKey?.value ]);

    const isAdmin = useSelector(isAdminSelector);
    const canManageLicensingData = useCanManageLicensingData();
    const showGenerateKey = isAdmin || canManageLicensingData;

    return (
        <div className={classes.apiKeyWithAction}>
            <div className={classes.apiKey}>
                <UiText
                    className={classes.apiKeyText}
                    data-cy={`licenses-api-key-label-${consumableCode.toLowerCase()}`}>
                    {translate({ id: 'CLIENT_API_KEY' })}
                  &nbsp;
                </UiText>
                {loading && <CircularProgress
                    size={20}
                    style={{ marginLeft: '8px' }} />}
                {!loading && <Tooltip
                    arrow
                    title={apiKey?.value && showKey ? apiKey.value : ''}>
                    <UiText
                        className={clsx({
                            [classes.apiKeyLine]: true,
                            [classes.noWrap]: true,
                            [classes.noEllipsis]: !!apiKey?.value && !showKey,
                        })}
                        data-cy={`licenses-api-key-value-${consumableCode.toLowerCase()}`}
                    >
                        {apiKey?.value
                            ? (showKey ? apiKey.value : <>
                                <span
                                    aria-hidden="true"
                                    className={classes.maskedApiKey}>
                                    {MASKED_API_KEY}
                                </span>
                                <span className={classes.visuallyHidden}>
                                    {translate({ id: 'CLIENT_API_KEY_HIDDEN' })}
                                </span>
                            </>)
                            : translate({ id: 'CLIENT_NOT_GENERATED_YET' })}
                    </UiText>
                </Tooltip>}
            </div>
            <div className={classes.apiKeyActions}>
                {apiKey?.value && showGenerateKey && (
                    <Tooltip
                        title={translate({ id: showKey ? 'CLIENT_HIDE_API_KEY' : 'CLIENT_SHOW_API_KEY' })}
                        placement="top">
                        <IconButton
                            aria-label={translate({ id: showKey ? 'CLIENT_HIDE_API_KEY' : 'CLIENT_SHOW_API_KEY' })}
                            aria-pressed={showKey}
                            onClick={() => setShowKey(previous => !previous)}
                            data-cy={`licenses-toggle-key-visibility-${consumableCode.toLowerCase()}`}
                        >
                            {showKey
                                ? <VisibilityOffOutlinedIcon
                                    color="primary"
                                    fontSize="small" />
                                : <VisibilityOutlinedIcon
                                    color="primary"
                                    fontSize="small" />}
                        </IconButton>
                    </Tooltip>
                )}
                {apiKey?.value && showGenerateKey && (
                    <CopyToClipboard
                        text={apiKey.value}
                        onCopy={() => createNotification(translate({ id: 'CLIENT_COPIED_TO_CLIPBOARD' }), notificationType.SUCCESS)}>
                        <Tooltip
                            title={translate({ id: 'CLIENT_COPY_API_KEY' })}
                            placement="top">
                            <IconButton
                                aria-label={translate({ id: 'CLIENT_COPY_API_KEY' })}
                                style={{ marginLeft: '12px' }}>
                                <FileCopyOutlinedIcon
                                    data-cy={`licenses-copy-key-${consumableCode.toLowerCase()}`}
                                    color="primary"
                                    fontSize="small"
                                />
                            </IconButton>
                        </Tooltip>
                    </CopyToClipboard>
                )}
                {showGenerateKey && consumableCodeToApiKeyIdentifier[consumableCode] && (
                    <Tooltip
                        title={translate({ id: 'CLIENT_GENERATE_NEW' })}
                        placement="top">
                        <IconButton
                            className={classes.iconButton}
                            style={{ marginLeft: apiKey ? '12px' : '0' }}
                            onClick={() => regenerate(consumableCodeToApiKeyIdentifier[consumableCode])}
                            data-cy={`licenses-generate-key-${consumableCode.toLowerCase()}`}
                        >
                            <AutorenewIcon
                                color="primary"
                                fontSize="small" />
                        </IconButton>
                    </Tooltip>
                )}
            </div>
        </div>
    );
};
